import { Box, StandardTextFieldProps, TextField, Typography } from '@material-ui/core'
import { FormLabel } from 'common/atoms/FormLabel'
import { RequiredSign } from 'pages/content/components/jobs/components/atoms'
import { darken } from 'polished'
import { ChangeEventHandler, PropsWithChildren, ReactNode, useState } from 'react'
import styled from 'styled-components'
import { useTheme } from 'styled-components'

export interface IInputFieldGroupProps extends StandardTextFieldProps {
  label?: string | ReactNode
  placeholder?: string
  name?: string
  required?: boolean
  displaySwitch?: boolean
  switchChangeHandler?: ChangeEventHandler
  type?: string
  area?: boolean
  rowSize?: number
  cols?: number
  areaValue?: any
  wordCount?: number
  showCounter?: boolean
  switchName?: string
  switchValue?: boolean
  handler?: ChangeEventHandler
  charLimit?: number
  areaDisabled?: boolean
  width?: string
  withHelperText?: boolean
  disabled?: boolean
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  design?: 'small'
}

export interface ITextInputProps extends StandardTextFieldProps {
  width?: string
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  design?: 'small'
}

const TextInput = ({ width, startAdornment, endAdornment, design, ...rest }: ITextInputProps) => {
  return <StyledTextField InputProps={{ disableUnderline: true, startAdornment, endAdornment }} design={design} {...rest} autoComplete="off" />
}

export const StyledTextField = styled(TextField)<{ error?: boolean; design?: string; }>`
  && .MuiInputBase-root {
    background-color: ${props => props.theme.colors.bgPrimary};
    border: ${props => (props.error ? `1px solid ${props.theme.colors.error}` : '1px solid transparent')};
    border-radius: 12px;
    box-sizing: border-box;
    height: ${props => props.design == 'small' ? '32px' : ''};
  }

  && {
    border: 1px solid transparent;
    border-radius: 12px;
  }
  &&:has(:focus) {
    border: 1px solid ${props => (props.error ? 'transparent' : props.theme.colors.primary)} !important;
    border-radius: 12px;
  }

  && .MuiInputBase-input::placeholder {
    color: ${props => darken(0.3, props.theme.colors.accent)} !important;
    opacity: 1 !important;
  }

  && .MuiFormHelperText-root {
    color: ${props => props.theme.colors.accent1} !important;
    font-weight: 400;
    font-size: '0.75rem';
  }

  & .MuiInputBase-root.Mui-disabled {
    color: ${props => darken(0.35, props.theme.colors.accent)} !important;
  }

  && .MuiInputBase-input::placeholder {
    color: ${props => props.theme.colors.accent} !important;
    opacity: 0.6 !important;
  }
`
export const StyledInputContainer = styled(Box)<{ disabled?: boolean; error?: boolean; design?: string; }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: ${props => props.theme.colors.accent};
  width: 100%;
  transition: opacity 0.25s;
  opacity: ${props => (props.disabled ? 0.3 : 1)};

  & .MuiTypography-caption {
    padding-left: 0.75rem;
  }
`

const StyledSubscript = styled(Typography)<{ maxed: boolean }>`
  color: ${props => (props.maxed ? 'red' : props.theme.colors.accent1)};
  font-size: 0.75rem;
`

export const InputFieldGroup = (props: PropsWithChildren<IInputFieldGroupProps>) => {
  const {
    width,
    label,
    placeholder,
    name,
    required,
    rowSize,
    area,
    wordCount,
    handler,
    switchChangeHandler,
    areaValue,
    switchName,
    showCounter = false,
    type = 'text',
    displaySwitch = false,
    charLimit = 10000,
    areaDisabled,
    switchValue,
    withHelperText,
    helperText,
    startAdornment,
    endAdornment,
    ...rest
  } = props

  const theme = useTheme()

  // Please note that in order to make sure that helperText takes more space than the counter, I do a type check to give a string the columns size value of 2fr.
  const gridColumns = [helperText, showCounter]
    .filter(text => !!text)
    .map(val => (typeof val === 'string' ? '2fr' : '1fr'))
    .join(' ')

  return (
    <StyledInputContainer disabled={rest.disabled} error={rest.error} design="small">
      {label && (
        <Box paddingLeft="0.75rem" display="flex" alignItems="center" justifyContent="start">
          <FormLabel>
            {label}
            <RequiredSign visible={required} />
          </FormLabel>
        </Box>
      )}
      <TextInput type={type} placeholder={placeholder} name={name} required={required} startAdornment={startAdornment} endAdornment={endAdornment} {...rest} />
      {(helperText || showCounter) && (
        <Box display="grid" gridTemplateColumns={gridColumns} gridTemplateRows="1fr">
          {helperText && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              color={theme.colors.accent1}
            >
              <Typography variant="caption" color={rest.error ? 'error' : 'inherit'}>
                {helperText}
              </Typography>
            </Box>
          )}
          {showCounter && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="flex-end"
              alignItems="flex-end"
              color={theme.colors.accent1}
              paddingRight="0.375rem"
            >
              <StyledSubscript variant="caption" maxed={wordCount >= charLimit ? true : false}>
                {(rest?.value as string)?.length ?? 0}/{charLimit}
              </StyledSubscript>
            </Box>
          )}
        </Box>
      )}
    </StyledInputContainer>
  )
}
